/* Los caminos de la barrera: qué se puede cargar y cuándo se usa cada uno.
 *
 * ── POR QUÉ ESTO EXISTE ──────────────────────────────────────────────────
 *
 * La pantalla eran OCHO baldosas iguales, en dos filas, con los rótulos en
 * mayúscula sostenida: MOV. DE TRACTORES · FLETEROS & EXTERNOS · MOV. DE
 * PROVEEDOR · LLAVES · PERSONA · TRACTOR · FURGON · EMPRESA. Todas del mismo
 * tamaño, todas del mismo color, sin una palabra de cuándo se usa cada una.
 *
 * Y portería carga **con la barrera abajo y un camión esperando**. En esa
 * situación, ocho opciones sin jerarquía son ocho cosas que hay que leer y
 * descartar antes de poder empezar — y lo que no se carga en el momento no se
 * carga nunca.
 *
 * ── LAS OCHO NO ERAN OCHO: ERAN CUATRO Y CUATRO ──────────────────────────
 *
 * Eso es lo que la grilla escondía. Las de arriba son **el trabajo**: anotar
 * que algo cruzó. Las de abajo son **el padrón**: dar de alta un tractor, una
 * persona, una empresa. Son dos cosas distintas —una pasa cien veces por día
 * y la otra cuando falta algo— y puestas al mismo nivel compiten.
 *
 * Ahora hay UNA pregunta con cuatro respuestas, y las altas quedan debajo,
 * como lo que son: la salida para cuando el que cruzó todavía no existe en el
 * sistema.
 *
 * ── EL ORDEN NO ES ALFABÉTICO NI EL DE ANTES ─────────────────────────────
 *
 * Va de lo que más pasa a lo que menos. Un menú ordenado por otra cosa
 * obliga a recorrerlo entero todas las veces, y el que más pasa es el que
 * menos tiene que buscarse.
 *
 * ── Y CADA UNO DICE CUÁNDO SE USA ────────────────────────────────────────
 *
 * «FLETEROS & EXTERNOS» no dice si un camión de un tercero que viene a
 * cargar va ahí o en proveedores. Esa duda se resuelve preguntando, o
 * eligiendo mal — y las dos cuestan más que la línea de texto que la evita.
 */

/* `tipo` es la clave de `FormContent.eventos`, o sea qué campos se van a
 * pedir. Vive acá y no adentro de la pantalla porque es lo que hay que poder
 * verificar: un camino cuyo `tipo` no existe abre un formulario VACÍO y no
 * falla en ningún lado — ya nos pasó con el «editar» de un evento. */
export const CAMINOS = [
  {
    id: "flota",
    tipo: "porteria",
    icono: "camion",
    titulo: "Un tractor de la flota",
    cuando: "Entrada o salida de una unidad nuestra, con su chofer y su furgón.",
  },
  {
    id: "particular",
    tipo: "porteriaParticular",
    icono: "auto",
    titulo: "Un fletero o un externo",
    cuando:
      "Un camión que no es de la empresa, o un auto particular que entra al predio.",
  },
  {
    id: "proveedor",
    tipo: "porteriaProveedores",
    icono: "proveedor",
    titulo: "Un proveedor",
    cuando: "Viene a entregar o a retirar algo, y queda registrado a su nombre.",
  },
  {
    id: "llaves",
    tipo: "llaves",
    icono: "llave",
    titulo: "Entrega o devolución de llaves",
    cuando:
      "Quién se llevó las llaves de una unidad y quién las trajo de vuelta.",
  },
];

/* Las altas. NO son un camino de la barrera: son lo que se hace cuando lo que
 * cruzó todavía no existe en el sistema.
 *
 * Se muestran igual, y plegadas — no escondidas. Escondidas del todo, el que
 * tiene adelante un tractor que no está cargado no sabe que se puede resolver
 * desde acá y anota cualquier cosa en el detalle. Es la misma regla que el
 * rail: se pliega a mano, nunca se recorta solo. */
/* Los ids llevan `alta-` adelante, y no es prolijidad: `proveedor` ya
 * significa «un proveedor cruzó la barrera». La misma palabra para dos
 * acciones distintas es exactamente lo que hace que un día una se abra en
 * lugar de la otra — y acá una anota un movimiento y la otra crea una fila en
 * un padrón. */
export const ALTAS = [
  { id: "alta-persona", titulo: "Una persona", icono: "personas" },
  { id: "alta-tractor", titulo: "Un tractor", icono: "camion" },
  { id: "alta-furgon", titulo: "Un furgón", icono: "furgon" },
  /* El botón decía «EMPRESA» y abría el formulario de PROVEEDOR: dos nombres
   * para la misma cosa, y el que buscaba dar de alta a un cliente lo abría y
   * cargaba un proveedor. El rótulo dice ahora lo que la fila va a ser. */
  { id: "alta-proveedor", titulo: "Un proveedor", icono: "proveedor" },
];

/* El camino por su id. Devuelve `null` y no un default: caer en «tractor de
 * la flota» ante un id desconocido cargaría el movimiento en el formulario
 * equivocado, sin fallar. */
export const camino = (id) => CAMINOS.find((c) => c.id === id) || null;
